import { useCallback, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { moveWorkspaceFile } from '@/lib/api/generated/api';
import type {
  MoveFileRequest,
  MoveFileResult,
} from '@/lib/api/generated/model';
import { MOVE_CONFLICT, getMoveFailureCode } from '../errors';
import { nameOf, parentOf } from '../folders';
import { fileKeys } from '../queryKeys';

/**
 * Moves one file or folder within a workspace.
 *
 * The whole of moving lives here: callers hand over two paths and learn only
 * whether it is in flight. Three things they do not have to know about:
 *
 * - **Two listings change, not one.** The folder losing the item and the folder
 *   gaining it are separate cache entries, and invalidating only the one on
 *   screen leaves the other quietly wrong (FR-025).
 * - **A collision is the server's to detect.** Checking first would be a race —
 *   the destination can be taken between the check and the move — so the
 *   documented 409 drives the overwrite prompt instead (research R7).
 * - **Nothing is shown before it is known.** There is no optimistic removal; the
 *   row stays put and marked until the outcome arrives (FR-025a), and the marker
 *   is cleared whichever way it goes.
 */
export function useMoveWorkspaceFile(
  workspaceId: string,
  {
    onMoved,
    onConflict,
    onFailed,
  }: {
    onMoved: (result: MoveFileResult) => void;
    onConflict: (request: MoveFileRequest) => void;
    /** `request` is passed so a caller can tell which folder to refresh. */
    onFailed: (name: string, data: unknown, request: MoveFileRequest) => void;
  },
) {
  const queryClient = useQueryClient();
  const [pendingPaths, setPendingPaths] = useState<ReadonlySet<string>>(
    new Set(),
  );

  const clearPending = useCallback((path: string) => {
    setPendingPaths((previous) => {
      const next = new Set(previous);
      next.delete(path);
      return next;
    });
  }, []);

  const mutation = useMutation({
    mutationFn: async (request: MoveFileRequest) => {
      const response = await moveWorkspaceFile(workspaceId, request);
      if (response.status !== 200) {
        // Carried rather than flattened to a message: the caller decides what a
        // conflict means, and only it knows whether an overwrite may be offered.
        throw Object.assign(new Error('Move failed'), {
          data: response.data,
          request,
        });
      }
      return response.data;
    },
    onMutate: (request: MoveFileRequest) => {
      setPendingPaths((previous) => new Set(previous).add(request.source_path));
    },
    onSuccess: async (result: MoveFileResult, request) => {
      // Awaited, so the row is not handed back to the reader before the cache
      // agrees the move happened. `onSettled` clears the pending marker as soon
      // as this resolves, and clearing it early re-enabled drag, delete and
      // click on a row still drawn at its old path — a second action there
      // would address a path the service had already moved, and come back as a
      // "no longer there" error for what looked like an ordinary click.
      const parents = [
        parentOf(request.source_path),
        parentOf(request.destination_path),
      ].flatMap((path) => [
        queryClient.invalidateQueries({
          queryKey: fileKeys.listing(workspaceId, path),
        }),
        queryClient.invalidateQueries({
          queryKey: fileKeys.folderContents(workspaceId, path),
        }),
      ]);
      // A moved folder takes every listing beneath it along: `drafts/old` is
      // now `archive/drafts/old`, and the cached listing under the old path
      // would show contents the service no longer has there. Listing keys hold
      // the path as one element, so this cannot be a prefix match on the key —
      // the predicate walks the cache instead (research R4).
      const beneath =
        result.kind === 'directory'
          ? [
              queryClient.invalidateQueries({
                predicate: (query) =>
                  fileKeys.isUnderFolder(
                    query.queryKey,
                    workspaceId,
                    request.source_path,
                  ) ||
                  fileKeys.isUnderFolder(
                    query.queryKey,
                    workspaceId,
                    request.destination_path,
                  ),
              }),
            ]
          : [];
      await Promise.all([...parents, ...beneath]);
      onMoved(result);
    },
    onError: (error: unknown, request) => {
      const data = (error as { data?: unknown }).data;
      if (getMoveFailureCode(data) === MOVE_CONFLICT) {
        onConflict(request);
        return;
      }
      onFailed(nameOf(request.source_path), data, request);
    },
    onSettled: (_result, _error, request) => {
      clearPending(request.source_path);
    },
  });

  return {
    move: mutation.mutate,
    pendingPaths,
  };
}
